<template>
    <div :id="'_id'+message.id" class="flex group w-fit min-w-80 max-w-[85%] items-end gap-1.5 animate-fadeIn">
        
        <!-- Avatar simple style WhatsApp -->
        <div v-if="!selfMessage" class="flex-shrink-0 mb-1">
            <img 
                v-if="conversation?.profile" 
                :src="conversation.profile" 
                alt="Avatar"
                class="w-8 h-8 rounded-full object-cover"
            />
            <div 
                v-else
                class="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center"
            >
                <i class="las la-user text-gray-600 dark:text-gray-300 text-sm"></i>
            </div>
        </div>

        <!-- Contenu principal -->
        <div class="flex-1 min-w-0 space-y-1">
            
            <!-- Header avec métadonnées -->
            <div class="flex items-center justify-between gap-3 px-1">
                <!-- Badge transféré simple -->
                <transition name="slide-fade">
                    <div 
                        v-if="message.isForwarded" 
                        class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
                    >
                        <i class="fas fa-share text-xs"></i>
                        <span class="italic">Transféré</span>
                    </div>
                </transition>
                
                <!-- Agent name simple -->
                <div 
                    v-if="message?.agent?.firstname"
                    class="text-xs font-semibold ml-auto"
                    :class="selfMessage ? 'text-gray-700 dark:text-gray-300' : 'text-gray-700 dark:text-gray-300'"
                >
                    {{ message.agent.firstname }}
                </div>
            </div>

            <!-- Message cité avec nouveau design -->
            <transition name="slide-down">
                <div 
                    v-if="message?.parent" 
                    @click="scrollToParentMessage"
                    class="mx-1 group/quote cursor-pointer"
                >
                    <div class="relative overflow-hidden rounded-md bg-gray-100 dark:bg-[#2a3942] p-2 border-l-4 border-[#06cf9c] dark:border-[#00a884] transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-[#2d3d47]">
                        <div class="relative flex items-start gap-2">
                            <div class="flex-1 min-w-0 line-clamp-3">
                                <p class="text-[0.8125rem] font-medium text-[#06cf9c] dark:text-[#00a884] mb-0.5">
                                    En réponse à
                                </p>
                                <ReplyWidget :message="message.parent" />
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Bulle de message style WhatsApp -->
            <div 
                :class="[
                    'relative rounded-lg p-3 shadow-sm transition-all duration-200',
                    selfMessage 
                        ? 'bg-[#d9fdd3] dark:bg-[#005c4b] text-gray-900 dark:text-white ml-auto' 
                        : 'bg-white dark:bg-[#202c33] text-gray-900 dark:text-gray-100'
                ]"
                :style="selfMessage ? 'border-radius: 0.5rem 0.5rem 0.125rem 0.5rem;' : 'border-radius: 0.5rem 0.5rem 0.5rem 0.125rem;'"
            >

                <!-- Contenu du message -->
                <div class="relative">
                    <p 
                        :class="[
                            'text-[0.9375rem] leading-[1.3] whitespace-pre-wrap break-words',
                            selfMessage ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-gray-100'
                        ]"
                    >
                        {{ message.expanded ? (isJsonString(message.content) ? JSON.parse(message.content) : message.content) : truncatedContent(JSON.parse(message?.content) ?? "") }}
                    </p>
                    
                    <!-- Bouton Voir plus/moins -->
                    <button 
                        v-if="isJsonString(message.content) && JSON.parse(message.content)?.length > maxLength"
                        @click="toggleExpand(message)" 
                        :class="[
                            'mt-1 text-xs font-medium transition-colors duration-200',
                            selfMessage 
                                ? 'text-[#027eb5] dark:text-[#53bdeb]' 
                                : 'text-[#027eb5] dark:text-[#53bdeb]'
                        ]"
                    >
                        {{ message.expanded ? 'Voir moins' : 'Voir plus' }}
                    </button>
                </div>

                <!-- Footer avec timestamp style WhatsApp -->
                <div class="flex items-center justify-end gap-1 mt-1">
                    <span 
                        :class="[
                            'text-[0.6875rem] leading-none',
                            selfMessage ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'
                        ]"
                    >
                        {{ hour }}
                    </span>
                    
                    <!-- Status pour messages envoyés (double check WhatsApp style) -->
                    <div v-if="selfMessage" class="flex items-center">
                        <svg viewBox="0 0 16 15" width="16" height="15" class="text-gray-600 dark:text-gray-400">
                            <path fill="currentColor" d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"></path>
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bouton répondre simple -->
        <button
            @click="setParentReply"
            :class="[
                'opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0 mb-2',
                'w-7 h-7 rounded-full flex items-center justify-center',
                'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600',
                selfMessage ? 'order-first' : ''
            ]"
            title="Répondre"
        >
            <i class="fas fa-reply text-xs text-gray-600 dark:text-gray-300"></i>
        </button>

    </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import ReplyWidget from './ReplyWidget.vue';

export default {
    name: "ChatMessage",
    
    components: {
        ReplyWidget
    },
    
    data() {
        return {
            maxLength: 400,
        };
    },
    
    props: {
        conversation: {
            type: Object,
            required: true
        },
        message: {
            type: Object,
            required: true
        },
        hour: {
            type: String,
            required: true
        },
        selfMessage: {
            type: Boolean,
            default() {
                return false
            }
        }
    },
    
    computed: {
        ...mapFields("messages/reply", {
            parentReply: "parentReply"
        })
    },
    
    methods: {
        isJsonString(str) {
            try {
                JSON.parse(str);
                return true;
            } catch (e) {
                return false;
            }
        },
        
        truncatedContent(content) {
            if (!content) return "";
            return content.length > this.maxLength ? content.slice(0, this.maxLength) + '...' : content;
        },
        
        toggleExpand(message) {
            if (!("expanded" in message)) {
                message.expanded = false;
            }
            message.expanded = !message.expanded;
        },
        
        setParentReply() {
            this.parentReply = this.message;
        },
        
        scrollToParentMessage() {
            console.log('scrolling to parent...');
            document.querySelector(`#_id${this.message?.parent_intern_id}`)?.scrollIntoView({ 
                behavior: "smooth", 
                block: "end", 
                inline: "nearest" 
            });
        }
    }
}
</script>

<style scoped>
/* Animations d'entrée */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

/* Transitions pour les éléments conditionnels */
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
    transform: translateX(-10px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateX(10px);
    opacity: 0;
}

.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.2s ease-in;
}

.slide-down-enter-from {
    transform: translateY(-10px);
    opacity: 0;
}

.slide-down-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

/* Shadow simple */
.shadow-3xl {
    box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
}
</style>